import { useDeferredValue, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, MagnifyingGlass, ShieldWarning, TrendDown, TrendUp } from '@phosphor-icons/react'
import { apiRequest } from '../api/client'
import type { ActivePlan, ExerciseDetail, ExerciseSearch } from '../api/types'
import { groupExercisesByFamily } from '../app/redesignDerivations'
import { EmptyState, ErrorState, PageLoader } from '../components/States'

function MarkdownText({ value }: { value: string }) {
  return <div className="prose-lite">{value.split(/\r?\n/).filter(Boolean).map((line, index) => <p key={`${index}-${line.slice(0, 12)}`}>{line.replace(/^#+\s*/, '').replace(/^[-*]\s*/, '')}</p>)}</div>
}

export function LibraryPage() {
  const [query, setQuery] = useState('')
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [familyFilter, setFamilyFilter] = useState('all')
  const deferredQuery = useDeferredValue(query)
  const search = useQuery({
    queryKey: ['exercise-search', deferredQuery],
    queryFn: () => apiRequest<ExerciseSearch>(`/catalog/exercises?query=${encodeURIComponent(deferredQuery)}&limit=60`),
  })
  const activePlan = useQuery({
    queryKey: ['active-plan'],
    queryFn: () => apiRequest<ActivePlan>('/plans/active'),
    retry: false,
  })
  const detail = useQuery({
    queryKey: ['exercise-detail', selectedSlug],
    queryFn: () => apiRequest<ExerciseDetail>(`/catalog/exercises/${encodeURIComponent(selectedSlug!)}`),
    enabled: Boolean(selectedSlug),
  })

  const families = useMemo(() => [...new Set((search.data?.items ?? []).map((item) => item.familyCode))].sort((left, right) => left.localeCompare(right, 'en')), [search.data])
  const visibleItems = familyFilter === 'all' ? (search.data?.items ?? []) : (search.data?.items ?? []).filter((item) => item.familyCode === familyFilter)
  const groupedItems = groupExercisesByFamily(visibleItems)
  const activeExerciseIds = new Set(activePlan.data?.items.map((item) => item.exerciseId) ?? [])

  return (
    <div className="page-stack library-page">
      <div className="screen-kicker">
        <span>03 / Cviky</span>
        <span>{selectedSlug ? 'Detail katalogu' : 'Katalog pohybů'}</span>
      </div>

      <header className="library-hero">
        <div>
          <p className="eyebrow">Anglické názvy / české návody</p>
          <h1>{selectedSlug ? 'Detail cviku' : 'Knihovna cviků'}</h1>
          <p>Technika, vybavení a přesné pořadí regresí a progresí.</p>
        </div>
        {selectedSlug && <button className="button button--quiet" type="button" onClick={() => setSelectedSlug(null)}><ArrowLeft size={18} aria-hidden="true" /> Zpět do knihovny</button>}
      </header>

      {!selectedSlug && (
        <>
          <section className="library-tools" aria-labelledby="library-search-label">
            <label id="library-search-label" htmlFor="exercise-search">Hledat v katalogu</label>
            <div className="search-shell">
              <MagnifyingGlass size={20} aria-hidden="true" />
              <input id="exercise-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Pull-up, bench nebo český alias…" maxLength={100} type="search" />
              {search.data && <span>{search.data.total} výsledků</span>}
            </div>
            {families.length > 1 && (
              <div className="library-filters" role="group" aria-label="Filtrovat načtené výsledky podle pohybové rodiny">
                <button type="button" className={familyFilter === 'all' ? 'is-active' : ''} onClick={() => setFamilyFilter('all')}>Vše</button>
                {families.map((family) => <button type="button" className={familyFilter === family ? 'is-active' : ''} onClick={() => setFamilyFilter(family)} key={family}>{family}</button>)}
              </div>
            )}
          </section>

          {search.isPending ? <PageLoader label="Prohledávám katalog…" /> : search.isError ? <ErrorState error={search.error} onRetry={() => search.refetch()} /> : visibleItems.length === 0 ? <EmptyState title="Nic jsem nenašel" text="Zkus kratší název, český alias nebo jiný filtr." /> : (
            <div className="library-groups">
              {groupedItems.map((group, groupIndex) => (
                <section className="library-group" key={group.familyCode} aria-labelledby={`library-family-${groupIndex}`}>
                  <div className="library-group__heading">
                    <h2 id={`library-family-${groupIndex}`}><span>{String(groupIndex + 1).padStart(2, '0')} /</span> {group.familyCode}</h2>
                    <span>{group.exercises.length} cviků</span>
                  </div>
                  <div className="exercise-library-table">
                    <div className="library-table-head" aria-hidden="true"><span>#</span><span>Cvik</span><span>Osa</span><span>Vybavení</span><span>Stav</span><span /></div>
                    {group.exercises.map((exercise, index) => (
                      <button type="button" className="library-item" key={exercise.id} onClick={() => setSelectedSlug(exercise.slug)}>
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <span><strong>{exercise.name}</strong><small>{exercise.kind}</small></span>
                        <span>{exercise.axis}</span>
                        <span>{exercise.equipment.length > 0 ? exercise.equipment.join(', ') : 'Bez vybavení'}</span>
                        <span>{activeExerciseIds.has(exercise.id) ? 'V plánu' : exercise.isExpertOnly ? <><ShieldWarning size={16} aria-hidden="true" /> Expert</> : 'Katalog'}</span>
                        <ArrowRight size={17} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {selectedSlug && (detail.isPending ? <PageLoader label="Načítám detail cviku…" /> : detail.isError ? <ErrorState error={detail.error} onRetry={() => detail.refetch()} /> : (
        <div className="exercise-detail-layout">
          <article className="exercise-detail-main">
            <header className="exercise-detail-header">
              <p className="eyebrow">{detail.data.exercise.familyCode} / {detail.data.exercise.axis}</p>
              <h2>{detail.data.exercise.name}</h2>
              <div className="tag-list">{detail.data.exercise.equipment.length === 0 ? <span>Bez vybavení</span> : detail.data.exercise.equipment.map((item) => <span key={item}>{item}</span>)}</div>
            </header>
            <section className="detail-copy"><h3><span aria-hidden="true">01 /</span> Technika</h3><MarkdownText value={detail.data.techniqueMarkdown} /></section>
            <section className="detail-copy"><h3><span aria-hidden="true">02 /</span> Klíčové body</h3><MarkdownText value={detail.data.cuesMarkdown} /></section>
          </article>
          <aside className="exercise-detail-side">
            <section>
              <h3><TrendDown size={18} aria-hidden="true" /> Lehčí varianty</h3>
              {detail.data.regressions.length === 0 ? <p className="muted">Bez katalogové regrese.</p> : detail.data.regressions.map((item) => <button type="button" key={item.exerciseId} onClick={() => setSelectedSlug(item.slug)}>{item.name}<ArrowRight size={16} aria-hidden="true" /></button>)}
            </section>
            <section>
              <h3><TrendUp size={18} aria-hidden="true" /> Těžší varianty</h3>
              {detail.data.progressions.length === 0 ? <p className="muted">Bez katalogové progrese.</p> : detail.data.progressions.map((item) => <button type="button" key={item.exerciseId} onClick={() => setSelectedSlug(item.slug)}>{item.name}<ArrowRight size={16} aria-hidden="true" /></button>)}
            </section>
            {detail.data.chainSteps.length > 0 && <section><h3>Celé pořadí progrese</h3><ol className="chain-list">{[...detail.data.chainSteps].sort((a, b) => a.sortOrder - b.sortOrder).map((step) => <li className={step.exerciseId === detail.data.exercise.id ? 'is-current' : ''} key={step.stepId}><span>{step.sortOrder + 1}</span><button type="button" onClick={() => setSelectedSlug(step.exerciseSlug)}>{step.exerciseName}</button></li>)}</ol></section>}
          </aside>
        </div>
      ))}
    </div>
  )
}